import { useState, useEffect } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal, Eye, Trash2, UserRoundCog, Mail, Users } from 'lucide-react';
import { DataTablePagination } from '@/components/ui/data-table/pagination';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useUpdateAccountFlag } from '@/hooks/use-accounts';
import { useImpersonate } from '@/hooks/use-auth';
import { toast } from '@/components/ui/sonner';
import { accountUsersService } from '@/services';
import type { AccountResponseDto, AccountUser } from '@/types';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { Spinner } from '@/components/ui/spinner';

// ============================================================================
// Helpers
// ============================================================================

const formatDate = (dateString: string): string => {
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateString;
  }
};

function getInitials(name: string) {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-violet-100 text-violet-700 border-violet-200',
  agent: 'bg-sky-100 text-sky-700 border-sky-200',
  sentinel: 'bg-cyan-100 text-cyan-700 border-cyan-200'
};

// ============================================================================
// Account Users Sheet
// ============================================================================

interface AccountUsersSheetProps {
  account: AccountResponseDto | null;
  onClose: () => void;
}

function AccountUsersSheet({ account, onClose }: AccountUsersSheetProps) {
  const impersonateMutation = useImpersonate();
  const [users, setUsers] = useState<AccountUser[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!account) return;
    setIsLoading(true);
    setUsers([]);
    accountUsersService
      .getAccountUsersForAccount(String(account.id))
      .then(setUsers)
      .catch((err) => {
        const status = (err as { response?: { status?: number } })?.response?.status;
        const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? '';
        if (status === 403 && message.includes('another account')) {
          toast.error('You can only view users in your own account.');
        } else {
          toast.error(`Failed to load users for ${account.name}`);
        }
      })
      .finally(() => setIsLoading(false));
  }, [account]);

  const handleImpersonate = async (user: AccountUser) => {
    try {
      // Reloads the app on success (see useImpersonate).
      await impersonateMutation.mutateAsync({ userId: String(user.id), accountId: account?.id ? String(account.id) : undefined });
    } catch {
      toast.error('Failed to impersonate user.');
    }
  };

  return (
    <Sheet open={!!account} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className='sm:max-w-md w-[400px] flex flex-col overflow-hidden'>
        <SheetHeader className='pb-0 shrink-0'>
          <div className='flex items-center gap-3 mb-1'>
            <div className='flex h-9 w-9 items-center justify-center rounded-full bg-muted font-semibold text-sm shrink-0'>
              {account?.name.slice(0, 2).toUpperCase()}
            </div>
            <div className='min-w-0'>
              <SheetTitle className='text-base truncate'>{account?.name}</SheetTitle>
              <SheetDescription className='text-xs mt-0.5 truncate'>
                {account?.sub_domain
                  ? `${account.sub_domain} · ${users.length} member${users.length !== 1 ? 's' : ''}`
                  : `${users.length} member${users.length !== 1 ? 's' : ''}`}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <Separator className='my-4 shrink-0' />

        <div className='flex-1 overflow-y-auto'>
          {isLoading ? (
            <div className='space-y-3'>
              {[1, 2, 3].map((i) => (
                <div key={i} className='flex items-center gap-3 px-1'>
                  <Skeleton className='h-8 w-8 rounded-full shrink-0' />
                  <div className='flex-1 space-y-1.5'>
                    <Skeleton className='h-3 w-28' />
                    <Skeleton className='h-3 w-40' />
                  </div>
                  <Skeleton className='h-5 w-14 rounded-full shrink-0' />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <Users className='h-8 w-8 text-muted-foreground/40 mb-2' />
              <p className='text-sm font-medium'>No members found</p>
              <p className='text-xs text-muted-foreground mt-1'>This account has no users yet.</p>
            </div>
          ) : (
            <div className='space-y-1'>
              {users.map((user) => (
                <div
                  key={user.id}
                  className='flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-muted/50 transition-colors group'
                >
                  <Avatar className='h-8 w-8 shrink-0'>
                    <AvatarFallback className='text-xs font-medium bg-muted'>{getInitials(user.name)}</AvatarFallback>
                  </Avatar>
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate'>{user.name}</p>
                    <p className='text-xs text-muted-foreground truncate flex items-center gap-1'>
                      <Mail className='h-3 w-3 shrink-0' />
                      {user.email}
                    </p>
                  </div>
                  <div className='flex items-center gap-2 shrink-0'>
                    <span
                      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${ROLE_COLORS[user.role] ?? ''}`}
                    >
                      {user.role}
                    </span>
                    {/* Only the read-only admin membership can be impersonated (backend enforces it too). */}
                    {user.role === 'readonly_admin' && (
                      <Button
                        size='sm'
                        variant='ghost'
                        className='h-7 text-xs  group-hover:opacity-100 transition-opacity'
                        disabled={impersonateMutation.isPending}
                        onClick={() => handleImpersonate(user)}
                      >
                        <UserRoundCog className='mr-1 h-3.5 w-3.5' />
                        Impersonate
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

// Shadow Mode Cell Component
function ShadowModeCell({ account }: { account: AccountResponseDto }) {
  const updateFlagMutation = useUpdateAccountFlag();
  const shadowModeEnabled = (account.flag?.shadow_mode_enabled as boolean) ?? false;

  const handleToggle = async (checked: boolean) => {
    try {
      await updateFlagMutation.mutateAsync({
        accountId: account.id,
        flagKey: 'shadow_mode_enabled',
        flagValue: checked
      });
      toast.success(`Shadow mode ${checked ? 'enabled' : 'disabled'} for ${account.name}`);
    } catch (error) {
      console.error('Failed to update shadow mode:', error);
      toast.error('Failed to update shadow mode. Please try again.');
    }
  };

  return <Switch checked={shadowModeEnabled} onCheckedChange={handleToggle} disabled={updateFlagMutation.isPending} />;
}

// V3 Cell Component
function V3Cell({ account }: { account: AccountResponseDto }) {
  const updateFlagMutation = useUpdateAccountFlag();
  const v3Enabled = (account.flag?.v3 as boolean) ?? false;
  const isDisabled = updateFlagMutation.isPending;

  const handleToggle = async (checked: boolean) => {
    try {
      await updateFlagMutation.mutateAsync({
        accountId: account.id,
        flagKey: 'v3',
        flagValue: checked
      });
      toast.success(`V3 ${checked ? 'enabled' : 'disabled'} for ${account.name}`);
    } catch (error) {
      console.error('Failed to update V3:', error);
      toast.error('Failed to update V3. Please try again.');
    }
  };

  return <Switch checked={v3Enabled} onCheckedChange={handleToggle} disabled={isDisabled} />;
}

// AI Settings Enabled Cell Component
function AiSettingsEnabledCell({ account }: { account: AccountResponseDto }) {
  const updateFlagMutation = useUpdateAccountFlag();
  const aiSettingsEnabled = (account.flag?.ai_settings_enabled as boolean) ?? false;

  const handleToggle = async (checked: boolean) => {
    try {
      await updateFlagMutation.mutateAsync({
        accountId: account.id,
        flagKey: 'ai_settings_enabled',
        flagValue: checked
      });
      toast.success(`AI Settings ${checked ? 'enabled' : 'disabled'} for ${account.name}`);
    } catch (error) {
      console.error('Failed to update AI Settings:', error);
      toast.error('Failed to update AI Settings. Please try again.');
    }
  };

  return <Switch checked={aiSettingsEnabled} onCheckedChange={handleToggle} disabled={updateFlagMutation.isPending} />;
}

// ============================================================================
// Actions Cell
// ============================================================================

interface ActionsCellProps {
  account: AccountResponseDto;
  onViewUsers: (account: AccountResponseDto) => void;
}

function ActionsCell({ account, onViewUsers }: ActionsCellProps) {
  console.log('account', account);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant='ghost' className='h-8 w-8 p-0' onClick={(e) => e.stopPropagation()}>
          <span className='sr-only'>Open menu</span>
          <MoreHorizontal className='h-4 w-4' />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end'>
        <DropdownMenuItem
          onClick={(e) => {
            e.stopPropagation();
            onViewUsers(account);
          }}
        >
          <Eye className='mr-2 h-4 w-4' />
          View Users
        </DropdownMenuItem>
        <DropdownMenuItem onClick={(e) => e.stopPropagation()} className='text-destructive focus:text-destructive'>
          <Trash2 className='mr-2 h-4 w-4' />
          Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface AccountsTableProps {
  accounts: AccountResponseDto[];
  isLoading?: boolean;
  page: number;
  limit: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
}

export function AccountsTable({
  accounts,
  isLoading,
  page,
  limit,
  totalPages,
  onPageChange,
  onPageSizeChange
}: AccountsTableProps) {
  const [selectedAccount, setSelectedAccount] = useState<AccountResponseDto | null>(null);

  const columns: ColumnDef<AccountResponseDto>[] = [
    {
      accessorKey: 'id',
      header: 'Account ID',
      cell: ({ row }) => {
        return <div className='font-mono text-sm text-muted-foreground'>{row.getValue('id')}</div>;
      }
    },
    {
      accessorKey: 'name',
      header: 'Account Name',
      cell: ({ row }) => {
        return <div className='font-medium'>{row.getValue('name')}</div>;
      }
    },
    {
      accessorKey: 'sub_domain',
      header: 'Sub Domain',
      cell: ({ row }) => {
        const subDomain = row.getValue('sub_domain') as string | null;
        return <div className='text-muted-foreground'>{subDomain || '-'}</div>;
      }
    },
    {
      id: 'shadow_mode',
      header: 'Shadow Mode',
      cell: ({ row }) => {
        return <ShadowModeCell account={row.original} />;
      }
    },
    {
      id: 'v3',
      header: 'V3',
      cell: ({ row }) => {
        return <V3Cell account={row.original} />;
      }
    },
    {
      id: 'ai_settings_enabled',
      header: 'AI Settings',
      cell: ({ row }) => {
        return <AiSettingsEnabledCell account={row.original} />;
      }
    },
    {
      accessorKey: 'created_at',
      header: 'Created Date',
      cell: ({ row }) => {
        const date = row.getValue('created_at') as string;
        const formattedDate = formatDate(date);
        return <div className='text-muted-foreground'>{formattedDate}</div>;
      }
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        return <ActionsCell account={row.original} onViewUsers={setSelectedAccount} />;
      }
    }
  ];

  const table = useReactTable({
    data: accounts,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    state: {
      pagination: {
        pageIndex: page - 1,
        pageSize: limit
      }
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize: limit }) : updater;
      if (newPagination.pageIndex !== undefined && newPagination.pageIndex !== page - 1) {
        onPageChange(newPagination.pageIndex + 1);
      }
      if (newPagination.pageSize !== undefined && newPagination.pageSize !== limit) {
        onPageSizeChange(newPagination.pageSize);
      }
    }
  });

  return (
    <>
      <div className='space-y-4'>
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-48 text-center'>
                  <div className='flex flex-col items-center justify-center gap-2'>
                    <Spinner className='size-6' />
                    <span className='text-sm text-muted-foreground'>Loading accounts...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className='cursor-pointer'
                  onClick={() => setSelectedAccount(row.original)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-24 text-center text-muted-foreground'>
                  No accounts found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <DataTablePagination table={table} pageSizeOptions={[10, 20, 30, 40, 50]} showRowSelection={false} />
      </div>

      <AccountUsersSheet account={selectedAccount} onClose={() => setSelectedAccount(null)} />
    </>
  );
}
